/**
 * Canvas Chat — 类型定义
 *
 * 画布 AI 对话侧边栏的前端类型。
 */

// ============================================
// 聊天模式
// ============================================

export type ChatMode = 'general' | 'image' | 'video' | 'text';

export type SupportedChatMode = 'general' | 'video';

export type ChatGenerationIntent = 'image_subject' | 'image_subject_with_style';

export function normalizeChatMode(mode: ChatMode | string | null | undefined): SupportedChatMode {
  return mode === 'video' ? 'video' : 'general';
}

// ============================================
// Skill（场景化 AI 工作流）
// ============================================

export type SkillId =
  | 'ecommerce_video'
  | 'styling_expert'
  | 'fashion_design'
  | 'luxury_product'
  | 'ugc-bag-unboxing'
  | 'ugc-beauty-skincare'
  | 'ugc-try-on'
  | 'brand-visual-video'
  | 'video-adapt'
  | 'content-playbook'
  | 'skill-ugc-bag-unboxing'
  | 'skill-ugc-beauty-skincare'
  | 'skill-ugc-try-on'
  | 'skill-brand-visual-video'
  | 'skill-video-adapt'
  | 'skill-commerce-content-pack'
  | 'skill-batch-image-generation'
  | 'skill-product-research';
export type SkillAction =
  | 'research'
  | 'run'
  | 'intake'
  | 'intake_answer'
  | 'strategy'
  | 'script'
  | 'storyboard'
  | 'keyframes'
  | 'qa'
  | 'clips'
  | 'assembly';
export type SkillResultLane = 'research_ref' | 'image_candidate' | 'video_draft' | 'final_pick' | 'filtered';

export type VideoDuration =
  '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12' | '13' | '14' | '15'
  | '16' | '17' | '18' | '19' | '20' | '21' | '22' | '23' | '24' | '25' | '26' | '27' | '28' | '29' | '30';

/** 视频画面比例 */
export type VideoAspectRatio = 'adaptive' | '16:9' | '9:16' | '1:1' | '4:3' | '3:4' | '21:9';

/** 视频生成 AI 引擎 */
export type VideoProvider = 'kling' | 'veo' | 'seedance' | 'hailuo';
export type VideoProviderPromptEnhance = 'disabled' | 'enabled';
export type ComposerSurface = 'general' | 'skills' | 'video';

/** Veo 生成质量 */
export type VeoQuality = 'fast' | 'quality';

/** Kling 视频型号 */
export type KlingVideoModel = 'kling-v3' | 'kling-v3-omni';

/** Kling 视频分辨率 */
export type KlingVideoResolution = '720P' | '768P' | '1080P' | '2K' | '4K';

/** Seedance 子型号 */
export type SeedanceModel = 'seedance-2.0' | 'seedance-2.5';

/** Seedance 分辨率 */
export type SeedanceResolution = '480P' | '720P' | '1080P' | '2K' | '4K';

/** 视频生成子模式 — 由逻辑 provider 映射到各上游接口 */
export type VideoSubMode = 'universal_ref' | 'first_frame' | 'last_frame' | 'first_last_frame' | 'smart_multi_frame';

// ============================================
// 图片生成参数（已注册 provider transport）
// ============================================

/** 已知生图模型。本地保留用于冷启动；运行时 catalog 仍是可用性权威。 */
export type KnownImageModel =
  | 'kling-3.0'
  | 'kling-3.0-omni'
  | 'gemini-3.1-lite'
  | 'gemini-3.0'
  | 'gpt-image-2-low'
  | 'gpt-image-2.5-sunburst-low'
  | 'seedream-4.5'
  | 'seedream-5.0-lite'
  | 'seedream-5.0-pro'
  | 'dobify-image-1.0';
export type ImageModel = KnownImageModel | (string & {});

/** 生图分辨率 */
export type ImageResolution = '512' | '1K' | '2K' | '4K';

/** 生图画面比例 */
export type ImageAspectRatio =
  | 'auto'
  | '1:1' | '2:3' | '3:2' | '3:4' | '4:3' | '4:5' | '5:4' | '9:16' | '16:9' | '21:9'
  | '9:21'
  | '1:4' | '1:8' | '4:1' | '8:1';

export type ImageOutputLimit = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export const DEFAULT_IMAGE_MODEL: ImageModel = 'gpt-image-2.5-sunburst-low';

/** 不同模型支持的参数范围 */
export const IMAGE_MODEL_SPECS: Record<string, {
  label: string;
  resolutions: ImageResolution[];
  aspectRatios: ImageAspectRatio[];
  maxReferenceImages: number;
  outputResolutionMode?: string;
  providerOutputsPerTask?: number;
  productOutputMode?: string;
  userSelectableOutputCount?: boolean;
  qualityValues?: number[];
  defaultQuality?: number | null;
  submitReady?: boolean;
  status?: string;
  availabilityStatus?: string;
}> = {
  'kling-3.0': {
    label: 'Kling 3.0',
    resolutions: ['1K', '2K'],
    aspectRatios: ['auto', '16:9', '9:16', '1:1', '4:3', '3:4', '3:2', '2:3', '21:9'],
    maxReferenceImages: 1,
    providerOutputsPerTask: 1,
    productOutputMode: 'single_image',
    userSelectableOutputCount: true,
  },
  'kling-3.0-omni': {
    label: 'Kling 3.0-Omni',
    resolutions: ['1K', '2K'],
    aspectRatios: ['auto', '16:9', '9:16', '1:1', '4:3', '3:4', '3:2', '2:3', '21:9'],
    maxReferenceImages: 10,
    providerOutputsPerTask: 1,
    productOutputMode: 'single_image',
    userSelectableOutputCount: true,
  },
  'gemini-3.1-lite': {
    label: 'Nano 2 Lite',
    resolutions: ['1K', '2K', '4K'],
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'],
    maxReferenceImages: 3,
    providerOutputsPerTask: 1,
    productOutputMode: 'single_image',
    userSelectableOutputCount: true,
  },
  'gemini-3.0': {
    label: 'Nano Pro',
    resolutions: ['1K', '2K', '4K'],
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'],
    maxReferenceImages: 14,
    providerOutputsPerTask: 1,
    productOutputMode: 'single_image',
    userSelectableOutputCount: true,
  },
  'gpt-image-2-low': {
    label: 'GPT Image2',
    resolutions: ['1K', '2K', '4K'],
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9', '21:9', '9:21'],
    maxReferenceImages: 16,
    providerOutputsPerTask: 1,
    productOutputMode: 'single_image',
    userSelectableOutputCount: true,
  },
  'dobify-image-1.0': {
    label: 'Dobify Image 1.0',
    resolutions: ['1K'],
    aspectRatios: ['1:1', '3:4', '4:3', '9:16', '16:9'],
    maxReferenceImages: 1,
    providerOutputsPerTask: 1,
    productOutputMode: 'single_image',
    userSelectableOutputCount: true,
  },
  'seedream-5.0-pro': {
    label: 'Seedream 5.0 Pro',
    resolutions: ['1K', '2K', '4K'],
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'],
    maxReferenceImages: 10,
    providerOutputsPerTask: 1,
    productOutputMode: 'single_image',
    userSelectableOutputCount: true,
    submitReady: false,
  },
  'seedream-5.0-lite': {
    label: 'Seedream 5.0 Lite',
    resolutions: ['2K', '4K'],
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'],
    maxReferenceImages: 14,
    providerOutputsPerTask: 1,
    productOutputMode: 'single_image',
    userSelectableOutputCount: true,
    submitReady: false,
  },
  'seedream-4.5': {
    label: 'Seedream 4.5',
    resolutions: ['2K', '4K'],
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'],
    maxReferenceImages: 14,
    providerOutputsPerTask: 1,
    productOutputMode: 'single_image',
    userSelectableOutputCount: true,
    submitReady: false,
  },
  'midjourney-v7': {
    label: 'Midjourney V7',
    resolutions: ['1K', '2K', '4K'],
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '9:16', '16:9', '21:9'],
    maxReferenceImages: 10,
    outputResolutionMode: 'explicit',
    providerOutputsPerTask: 4,
    productOutputMode: 'individual_candidates',
    userSelectableOutputCount: false,
    qualityValues: [0.5, 1, 2, 4],
    defaultQuality: 1,
    submitReady: false,
    status: 'planned',
  },
  'midjourney-v8-1': {
    label: 'Midjourney V8.1',
    resolutions: ['1K', '2K', '4K'],
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '9:16', '16:9', '21:9'],
    maxReferenceImages: 10,
    outputResolutionMode: 'explicit',
    providerOutputsPerTask: 4,
    productOutputMode: 'individual_candidates',
    userSelectableOutputCount: false,
    qualityValues: [1, 4],
    defaultQuality: 1,
    submitReady: false,
    status: 'planned',
  },
  'midjourney-niji-7': {
    label: 'Midjourney Niji 7',
    resolutions: ['1K', '2K', '4K'],
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '9:16', '16:9', '21:9'],
    maxReferenceImages: 10,
    outputResolutionMode: 'explicit',
    providerOutputsPerTask: 4,
    productOutputMode: 'individual_candidates',
    userSelectableOutputCount: false,
    qualityValues: [],
    defaultQuality: null,
    submitReady: false,
    status: 'planned',
  },
  'midjourney-v8-2': {
    label: 'Midjourney V8.2',
    resolutions: ['1K', '2K', '4K'],
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '9:16', '16:9', '21:9'],
    maxReferenceImages: 10,
    outputResolutionMode: 'explicit',
    providerOutputsPerTask: 4,
    productOutputMode: 'individual_candidates',
    userSelectableOutputCount: false,
    qualityValues: [1, 2, 3, 4],
    defaultQuality: 1,
    submitReady: false,
    status: 'planned',
  },
};

export type ImageModelDisplayOption = {
  value: ImageModel;
  labelKey: string;
  descKey: string;
  label?: string;
  description?: string;
  recommended?: boolean;
  provider?: string;
  status?: 'active' | 'beta' | 'planned';
};

export type MidjourneyVersion = 'v7' | 'v8.1' | 'niji_7' | 'v8.2';
export type MidjourneyQuality = 0.5 | 1 | 2 | 3 | 4;

// Cold-start specs keep the default and saved variants stable before the catalog arrives.
for (const family of ['sunburst', 'flare']) {
  for (const tier of ['low', 'medium', 'high', 'xhigh', 'max']) {
    IMAGE_MODEL_SPECS[`gpt-image-2.5-${family}-${tier}`] = {
      ...IMAGE_MODEL_SPECS['gpt-image-2-low'], label: `GPT Image2.5 ${family} ${tier}`,
      submitReady: false,
    };
  }
}

export function isImage25Model(model: unknown): model is ImageModel {
  return typeof model === 'string' && /^gpt-image-2\.5-(sunburst|flare)-(low|medium|high|xhigh|max)$/.test(model);
}

export const IMAGE25_FAMILIES = ['sunburst', 'flare'] as const;
export const IMAGE25_TIERS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;

/** Collapse presentation only; retain concrete catalog IDs for pricing and submission. */
export function collapseImage25ModelOptions(options: readonly ImageModelDisplayOption[]): ImageModelDisplayOption[] {
  const first = options.find((option) => isImage25Model(option.value));
  return options.flatMap((option) => !isImage25Model(option.value) ? [option]
    : option === first ? [{ ...option, label: 'GPT Image2.5' }] : []);
}

export const MIDJOURNEY_DEFAULT_MODEL: ImageModel = 'midjourney-v8-2';
export const MIDJOURNEY_VERSION_OPTIONS: readonly {
  value: MidjourneyVersion;
  model: ImageModel;
  label: string;
}[] = [
  { value: 'v7', model: 'midjourney-v7', label: 'V7' },
  { value: 'v8.1', model: 'midjourney-v8-1', label: 'V8.1' },
  { value: 'v8.2', model: 'midjourney-v8-2', label: 'V8.2' },
  { value: 'niji_7', model: 'midjourney-niji-7', label: 'Niji 7' },
] as const;

export function isMidjourneyImageModel(model: unknown): model is ImageModel {
  return model === 'midjourney-v7'
    || model === 'midjourney-v8-1'
    || model === 'midjourney-niji-7'
    || model === 'midjourney-v8-2';
}

export function getMidjourneyVersion(model: ImageModel): MidjourneyVersion {
  return MIDJOURNEY_VERSION_OPTIONS.find((candidate) => candidate.model === model)?.value ?? 'v8.2';
}

export function getMidjourneyModelForVersion(version: MidjourneyVersion): ImageModel {
  return MIDJOURNEY_VERSION_OPTIONS.find((candidate) => candidate.value === version)?.model
    ?? MIDJOURNEY_DEFAULT_MODEL;
}

export function getMidjourneyQualityValues(model: ImageModel): readonly MidjourneyQuality[] {
  const values = IMAGE_MODEL_SPECS[model]?.qualityValues ?? [1];
  return values.filter((value): value is MidjourneyQuality => (
    value === 0.5 || value === 1 || value === 2 || value === 3 || value === 4
  ));
}

export function normalizeMidjourneyQuality(model: ImageModel, quality: unknown): MidjourneyQuality {
  const supported = getMidjourneyQualityValues(model);
  return supported.includes(quality as MidjourneyQuality)
    ? quality as MidjourneyQuality
    : (IMAGE_MODEL_SPECS[model]?.defaultQuality as MidjourneyQuality | undefined) ?? supported[0] ?? 1;
}

export function supportsMidjourneyQuality(model: unknown): model is ImageModel {
  return isMidjourneyImageModel(model) && getMidjourneyQualityValues(model).length > 0;
}

export function supportsMidjourneyHd(model: unknown): boolean {
  return model === 'midjourney-v8-1' || model === 'midjourney-v8-2';
}

export function supportsMidjourneyDraft(model: unknown): boolean {
  return model === 'midjourney-v7';
}

export function collapseMidjourneyModelOptions(
  options: readonly ImageModelDisplayOption[],
): ImageModelDisplayOption[] {
  const firstMidjourneyIndex = options.findIndex((option) => isMidjourneyImageModel(option.value));
  if (firstMidjourneyIndex < 0) return [...options];
  const collapsed: ImageModelDisplayOption[] = [];
  options.forEach((option, index) => {
    if (!isMidjourneyImageModel(option.value)) {
      collapsed.push(option);
    } else if (index === firstMidjourneyIndex) {
      collapsed.push({
        ...option,
        value: MIDJOURNEY_DEFAULT_MODEL,
        label: 'Midjourney',
        recommended: options.some((candidate) => (
          isMidjourneyImageModel(candidate.value) && candidate.recommended === true
        )),
      });
    }
  });
  return collapsed;
}

export const IMAGE_MODEL_DISPLAY_META: Record<KnownImageModel, Omit<ImageModelDisplayOption, 'value'>> = {
  'gpt-image-2.5-sunburst-low': { labelKey: 'image25Name', descKey: 'image25Description' },
  'gpt-image-2-low': { labelKey: 'imageModelGptImage2Low', descKey: 'imageModelGptImage2LowDesc' },
  'seedream-4.5': { labelKey: 'imageModelSeedream45', descKey: 'imageModelSeedream45Desc', provider: 'tencent_mps' },
  'seedream-5.0-lite': { labelKey: 'imageModelSeedream50Lite', descKey: 'imageModelSeedream50LiteDesc', provider: 'tencent_mps' },
  'seedream-5.0-pro': { labelKey: 'imageModelSeedream50Pro', descKey: 'imageModelSeedream50ProDesc', provider: 'tencent_mps' },
  'gemini-3.1-lite': { labelKey: 'imageModelGemini31Lite', descKey: 'imageModelGemini31LiteDesc' },
  'gemini-3.0': { labelKey: 'imageModelGemini30', descKey: 'imageModelGemini30Desc' },
  'kling-3.0': { labelKey: 'imageModelKling30', descKey: 'imageModelKling30Desc' },
  'kling-3.0-omni': { labelKey: 'imageModelKling30Omni', descKey: 'imageModelKling30OmniDesc' },
  'dobify-image-1.0': { labelKey: 'imageModelDobify10', descKey: 'imageModelDobify10Desc' },
};

export const FALLBACK_IMAGE_MODEL_ORDER: readonly KnownImageModel[] = [
  'gpt-image-2.5-sunburst-low',
  'gpt-image-2-low',
  'gemini-3.1-lite',
  'gemini-3.0',
  'kling-3.0',
  'kling-3.0-omni',
] as const;

export const FALLBACK_IMAGE_MODEL_OPTIONS: readonly ImageModelDisplayOption[] = FALLBACK_IMAGE_MODEL_ORDER.map((value) => ({
  value,
  label: IMAGE_MODEL_SPECS[value].label,
  ...IMAGE_MODEL_DISPLAY_META[value],
}));

export type ImageModelCatalogDisplayEntry = {
  id: string;
  provider?: string;
  label_zh?: string;
  label_en?: string;
  order?: number;
  status?: 'active' | 'beta' | 'planned';
  submit_ready?: boolean;
  resolutions?: readonly string[];
  aspect_ratios?: readonly string[];
  max_reference_images?: number;
  output_resolution_mode?: string;
  provider_outputs_per_task?: number;
  product_output_mode?: string;
  user_selectable_output_count?: boolean;
  quality_values?: readonly number[];
  default_quality?: number | null;
  availability_status?: string;
};

const FALLBACK_IMAGE_MODEL_ORDER_INDEX = new Map<string, number>(
  FALLBACK_IMAGE_MODEL_ORDER.map((value, index) => [value, index]),
);

/** 将服务端目录完整转换为 UI 选项；未知模型不会被静态 fallback 列表过滤。 */
export function buildImageModelCatalogOptions(
  models: readonly ImageModelCatalogDisplayEntry[],
  locale: string,
  recommendedModel?: string,
): ImageModelDisplayOption[] {
  const seen = new Set<string>();
  return models
    .filter((model) => {
      const id = model.id.trim();
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .sort((a, b) => (
      (a.order ?? FALLBACK_IMAGE_MODEL_ORDER_INDEX.get(a.id) ?? 999)
      - (b.order ?? FALLBACK_IMAGE_MODEL_ORDER_INDEX.get(b.id) ?? 999)
    ))
    .map((model) => {
      const value = model.id.trim() as ImageModel;
      const knownMeta = IMAGE_MODEL_DISPLAY_META[value as KnownImageModel];
      const meta = knownMeta ?? {
        labelKey: 'imageModel',
        descKey: 'imageModelDynamicDesc',
      };
      const localizedLabel = locale === 'zh'
        ? (model.label_zh?.trim() || model.label_en?.trim())
        : (model.label_en?.trim() || model.label_zh?.trim());

      return {
        ...meta,
        value,
        // Known models use synchronized translation keys if the catalog omits a label.
        // Truly dynamic models retain their server label or canonical ID.
        label: localizedLabel || (knownMeta ? undefined : value),
        recommended: value === recommendedModel,
        provider: model.provider?.trim() || meta.provider,
        status: model.status,
      };
    });
}

export type ImageModelSpec = (typeof IMAGE_MODEL_SPECS)[string];

export function isImageModel(value: unknown): value is ImageModel {
  return typeof value === 'string'
    && Object.prototype.hasOwnProperty.call(IMAGE_MODEL_SPECS, value);
}

const SERVER_CATALOG_AUTHORITY_REQUIRED_IMAGE_MODELS = new Set<string>([
  ...Object.keys(IMAGE_MODEL_SPECS).filter(isImage25Model),
  'seedream-4.5',
  'seedream-5.0-lite',
  'seedream-5.0-pro',
]);

export function isImageModelCatalogEntrySubmitReady(model: {
  id: string;
  status?: string;
  submit_ready?: boolean;
  availability_status?: string;
}): boolean {
  if (SERVER_CATALOG_AUTHORITY_REQUIRED_IMAGE_MODELS.has(model.id)) {
    return model.submit_ready === true
      && model.status !== 'planned'
      && model.availability_status === 'active';
  }
  return model.status !== 'planned' && model.submit_ready !== false;
}

/**
 * Provider-gated models keep static capability metadata for rendering, but
 * only a current server catalog snapshot may make them submit-ready.
 */
export function resetImageModelCatalogAuthorization(): void {
  for (const modelId of SERVER_CATALOG_AUTHORITY_REQUIRED_IMAGE_MODELS) {
    const spec = IMAGE_MODEL_SPECS[modelId];
    if (spec) spec.submitReady = false;
  }
}

export function normalizeImageModel(
  value: unknown,
  fallback: ImageModel = DEFAULT_IMAGE_MODEL,
): ImageModel {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (
    normalized === 'gpt-image-2'
    || normalized === 'gpt-image-2-medium'
    || normalized === 'gpt-image-2-high'
    || normalized === 'image2'
  ) {
    return 'gpt-image-2-low';
  }
  const candidate = isImageModel(value) ? value : fallback;
  const candidateSpec = IMAGE_MODEL_SPECS[candidate];
  // The catalog arrives asynchronously. Preserve canonical Midjourney / Image2.5
  // selection during cold start; server catalog/admission remains the final
  // submit-ready authority and may still reject a disabled environment.
  if (isMidjourneyImageModel(candidate) || isImage25Model(candidate)) return candidate;
  return candidateSpec?.submitReady === false || candidateSpec?.status === 'planned' ? fallback : candidate;
}

export function getImageModelSpec(value: unknown): ImageModelSpec {
  return IMAGE_MODEL_SPECS[normalizeImageModel(value)];
}

export function getDefaultImageAspectRatio(value: unknown): ImageAspectRatio {
  const spec = getImageModelSpec(value);
  return spec.aspectRatios.includes('auto') ? 'auto' : spec.aspectRatios[0];
}

/** Stable local default used whenever the user switches image models. */
export function getDefaultImageResolution(value: unknown): ImageResolution {
  const spec = getImageModelSpec(value);
  // Provider-managed models intentionally expose no selectable resolution.
  // Keep a stable internal value; the request boundary omits it.
  return spec.resolutions.includes('1K')
    ? '1K'
    : spec.resolutions[0] ?? '1K';
}

export function isImageResolutionManagedByProvider(value: unknown): boolean {
  const spec = getImageModelSpec(value);
  return spec.outputResolutionMode === 'prompt_derived'
    || spec.outputResolutionMode === 'provider_default'
    || spec.resolutions.length === 0;
}

/** Canonical state applied atomically at every image-model selection boundary. */
export function getDefaultImageGenerationSettings(value: unknown): {
  model: ImageModel;
  resolution: ImageResolution;
  aspectRatio: ImageAspectRatio;
  quality: MidjourneyQuality;
  hd: boolean;
  draft: boolean;
} {
  const model = normalizeImageModel(value);
  return {
    model,
    resolution: getDefaultImageResolution(model),
    aspectRatio: getDefaultImageAspectRatio(model),
    quality: normalizeMidjourneyQuality(model, undefined),
    hd: false,
    draft: false,
  };
}

export function getImageModelDisplayLabel(value: ImageModel): string {
  return IMAGE_MODEL_SPECS[value]?.label ?? value;
}

export function registerImageModelCatalogSpecs(
  models: readonly {
    id: string;
    label_en?: string;
    label_zh?: string;
    resolutions?: readonly string[];
    aspect_ratios?: readonly string[];
    max_reference_images?: number;
    output_resolution_mode?: string;
    provider_outputs_per_task?: number;
    product_output_mode?: string;
    user_selectable_output_count?: boolean;
    quality_values?: readonly number[];
    default_quality?: number | null;
    submit_ready?: boolean;
    status?: string;
    availability_status?: string;
  }[],
) {
  // The endpoint is a complete availability snapshot. Missing or disabled
  // provider-gated models must revoke a previous in-memory authorization.
  resetImageModelCatalogAuthorization();
  for (const model of models) {
    const id = String(model.id || '').trim();
    if (!id) continue;
    const fallback = IMAGE_MODEL_SPECS[id];
    const requiresServerAuthority = SERVER_CATALOG_AUTHORITY_REQUIRED_IMAGE_MODELS.has(id);
    const serverAuthorized = isImageModelCatalogEntrySubmitReady(model);
    IMAGE_MODEL_SPECS[id] = {
      label: String(model.label_en || model.label_zh || fallback?.label || id),
      // An explicit empty list is meaningful for prompt-derived/provider-default
      // models (for example Midjourney); only an omitted field uses fallback data.
      resolutions: (model.resolutions !== undefined
        ? model.resolutions
        : fallback?.resolutions ?? ['1K']) as ImageResolution[],
      aspectRatios: ((model.aspect_ratios?.length ? model.aspect_ratios : fallback?.aspectRatios ?? ['auto', '1:1']) as ImageAspectRatio[]),
      maxReferenceImages: typeof model.max_reference_images === 'number'
        ? model.max_reference_images
        : fallback?.maxReferenceImages ?? 1,
      outputResolutionMode: model.output_resolution_mode ?? fallback?.outputResolutionMode,
      providerOutputsPerTask: model.provider_outputs_per_task ?? fallback?.providerOutputsPerTask ?? 1,
      productOutputMode: model.product_output_mode ?? fallback?.productOutputMode ?? 'single_image',
      userSelectableOutputCount: model.user_selectable_output_count ?? fallback?.userSelectableOutputCount ?? false,
      qualityValues: model.quality_values !== undefined
        ? [...model.quality_values]
        : fallback?.qualityValues,
      defaultQuality: model.default_quality !== undefined
        ? model.default_quality
        : fallback?.defaultQuality,
      submitReady: requiresServerAuthority
        ? serverAuthorized
        : model.submit_ready ?? fallback?.submitReady,
      status: model.status ?? fallback?.status,
      availabilityStatus: model.availability_status ?? fallback?.availabilityStatus,
    };
  }
}

/** Omit resolution at the wire boundary when the provider derives it from the prompt. */
export function getImageModelResolutionForRequest(
  model: ImageModel,
  selectedResolution: ImageResolution,
): ImageResolution | undefined {
  const spec = getImageModelSpec(model);
  if (isImageResolutionManagedByProvider(model)) return undefined;
  return spec.resolutions.includes(selectedResolution) ? selectedResolution : spec.resolutions[0];
}

/** 用户生成偏好（持久化到 user profile.preferences） */
export interface ImageGenPreferences {
  imageModel: ImageModel;
  imageResolution: ImageResolution;
  imageAspectRatio: ImageAspectRatio;
  midjourneyQuality: MidjourneyQuality;
  midjourneyHd: boolean;
  midjourneyDraft: boolean;
  videoProvider: VideoProvider;
  klingModel: KlingVideoModel;
  klingGenerateAudio: boolean;
  klingResolution: KlingVideoResolution;
  veoQuality: VeoQuality;
  seedanceModel: SeedanceModel;
  seedanceResolution: SeedanceResolution;
  videoDuration: VideoDuration;
}

/** Skill 视频生成偏好（按 Skill 独立持久化到 user profile.preferences） */
export interface SkillVideoPreference {
  provider: VideoProvider;
  model: string;
  duration: number;
  resolution: string;
  aspectRatio: string | null;
  generateAudio: boolean;
  narrationEnabled: boolean;
  narrationLanguage: 'zh' | 'en';
  storyboardEnabled: boolean;
}

export type SkillVideoPreferences = Record<string, SkillVideoPreference>;

// ============================================
// 对话线程
// ============================================

export interface ChatThread {
  id: string;
  project_id: string;
  user_id: string;
  title: string | null;
  skill_id?: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================
// 消息
// ============================================

export type ChatMessageRole = 'user' | 'assistant' | 'system';

export type ChatMessageStatus = 'sending' | 'streaming' | 'recovering' | 'done' | 'error' | 'interrupted';

export type ExploreHandoffIntent = 'create' | 'video_director';

export interface DirectorImageAttribute {
  facet: string;
  value: string;
  confidence?: number;
  source?: string;
}

export interface DirectorVisualMetadata {
  status?: 'pending' | 'analyzing' | 'completed' | 'failed';
  analyzedAt?: string;
  styles?: string[];
  moods?: string[];
  colors?: string[];
  scene?: string;
  lighting?: string;
  bodyFraming?: string;
  qualityScore?: number;
  description?: string;
  contentType?: string;
  faceVisibility?: string;
  isIsolatedObject?: boolean;
  useTags?: string[];
  adviseTags?: Array<{ id: string; confidence: number }>;
  error?: string;
}

export interface DirectorImageSignals {
  imageId?: string;
  assetId?: string;
  width?: number;
  height?: number;
  aspectRatio?: string;
  description?: string;
  attributes?: DirectorImageAttribute[];
  visualMetadata?: DirectorVisualMetadata;
  sourceLabel?: string;
}

export interface DirectorPlanReferenceProvenance {
  type: 'user_brief' | 'description' | 'image_attribute' | 'style_analysis' | 'dimensions';
  key: string;
  value: string;
}

export interface DirectorPlanShot {
  id: string;
  role: 'hook' | 'supporting' | 'cta';
  label: string;
  durationMs: number;
  objective: string;
  promptSeed: string;
  capabilityId?: string;
  capabilityLabel?: string;
  emphasis?: 'composition' | 'detail' | 'motion' | 'packaging';
  referenceProvenance: DirectorPlanReferenceProvenance[];
  status?: 'planned' | 'ready' | 'generated';
}

export interface DirectorPlanPackagingState {
  subtitlePreset: 'hook_subtitles' | 'minimal_subtitles' | 'none';
  titleCardText?: string;
  ctaText?: string;
  safeZonePreset: 'vertical_focus' | 'center_safe' | 'wide_master';
  hints: string[];
}

export interface DirectorPlan {
  stage: 'draft_ready' | 'animatic_ready';
  runtimeStatus: 'current_stack';
  contentProfile: 'portrait' | 'product' | 'scene' | 'editorial';
  targetAspectRatio: string;
  targetDurationMs: number;
  hookShot: DirectorPlanShot;
  supportingShots: DirectorPlanShot[];
  ctaOutro: DirectorPlanShot;
  mustKeepSignals: string[];
  packagingPresetIds: string[];
  variantPresetIds: string[];
  primaryFacts: string[];
  packaging: DirectorPlanPackagingState;
}

export interface VideoDirectorBeat {
  id: string;
  label: string;
  durationMs: number;
  objective: string;
  promptSeed: string;
  capabilityId?: string;
  capabilityLabel?: string;
  status?: 'planned' | 'ready' | 'generated';
}

export interface VideoDirectorVariantPreset {
  id: string;
  label: string;
  aspectRatio: string;
  useCase: string;
}

export interface VideoDirectorPackagingPreset {
  id: string;
  label: string;
  detail: string;
}

export interface VideoDirectorDraft {
  stage: 'draft_ready' | 'animatic_ready';
  runtimeStatus: 'current_stack';
  targetAspectRatio: string;
  targetDurationMs: number;
  beats: VideoDirectorBeat[];
  packagingPresets: VideoDirectorPackagingPreset[];
  variantPresets: VideoDirectorVariantPreset[];
  executionPriorities: string[];
}

export interface ExploreHandoff {
  source: 'explore' | 'asset';
  intent?: ExploreHandoffIntent;
  summary: string;
  creativeFocus: string;
  selectedImageCount: number;
  sourceNodeId?: string;
  imageSignals?: DirectorImageSignals;
  directorPlan?: DirectorPlan;
  directorDraft?: VideoDirectorDraft;
}

const VIDEO_DIRECTOR_SUMMARY_HINTS = [
  /short-video/i,
  /video direction/i,
  /shot planning/i,
  /packaging/i,
  /animatic/i,
  /短视频/,
  /分镜/,
  /包装/,
  /成片/,
] as const;

export function isVideoDirectorHandoff(handoff?: ExploreHandoff | null): boolean {
  if (!handoff) return false;
  if (handoff.intent === 'video_director') return true;
  if (handoff.directorPlan) return true;
  if (handoff.directorDraft) return true;
  return VIDEO_DIRECTOR_SUMMARY_HINTS.some((pattern) => pattern.test(handoff.summary));
}

export function normalizeExploreHandoff(handoff?: ExploreHandoff | null): ExploreHandoff | undefined {
  if (!handoff) return undefined;
  if (isVideoDirectorHandoff(handoff) && handoff.intent !== 'video_director') {
    return {
      ...handoff,
      intent: 'video_director',
    };
  }
  return handoff;
}

export interface ChatMessage {
  id: string;
  thread_id: string;
  /** 同一轮用户与助手消息共享的稳定请求标识，用于历史记录确定性排序。 */
  client_request_id?: string | null;
  role: ChatMessageRole;
  content: string;

  /** 用户消息引用的画布节点 */
  attached_nodes: AttachedNodeRef[];

  /** 用户消息选择的自定义风格 */
  selected_custom_style?: import('@/types/custom-style').SelectedCustomStyle | null;

  /** 用户消息选择的 Product / Character 模型 */
  selected_subject_models?: import('@/types/subject-model').SelectedSubjectModels | null;

  /** 用户消息触发的 Skill 工作流 */
  skill_id?: string | null;

  /** UI 发起的结构化动作；显示层不得再从中英文消息文本反推动作类型。 */
  structured_action?: {
    reference_context?: 'current_turn' | null;
    kind?: 'skill' | string;
    /** UI-issued continuation commands are execution state, not user-authored chat copy. */
    visibility?: 'internal' | 'user' | string;
    skill_id?: string | null;
    skill_action?: string | null;
    source_skill_artifact_id?: string | null;
    creative_intake_action?: {
      action?: string | null;
      decision_key?: string | null;
      value?: string | null;
      video_generation?: Record<string, unknown> | null;
      revision?: number | null;
    } | null;
    storyboard_id?: string | null;
    storyboard_revision?: number | null;
    brand_storyboard?: Record<string, unknown> | null;
    ugc_assembly_result?: {
      sequence_node_id: string;
      export_status: 'completed' | 'failed';
      output_url?: string;
      error?: string;
    } | null;
    /** Exact product-detail replay contract selected before generation. */
    product_target?: import('@/lib/api/canvas-chat').ProductTargetSelection | null;
    product_detail_page_count?: number | null;
    requested_output_count?: number | null;
  } | null;

  /** AI 消息附带的生成结果 */
  generated_results: GeneratedResult[];

  /** AI 思考步骤（可折叠展示） */
  thinking_steps: ThinkingStep[];

  /** 跟进建议（AI 消息末尾的可点击建议） */
  suggestions?: (string | ActionSuggestion)[];

  /** 建议正在生成中（前端运行时状态） */
  suggestionsLoading?: boolean;

  /** 执行计划卡片（AI 消息中的任务摘要） */
  task_plans?: TaskPlan[];

  /** LLM token 消耗 */
  token_usage?: TokenUsage;

  created_at: string;

  // --- 前端运行时状态（不持久化） ---
  status?: ChatMessageStatus;

  /** 错误描述（status='error' 时） */
  errorMessage?: string;

  /** 待确认的执行计划（用户确认后才执行） */
  pending_plan?: PendingPlan;

  /** 素材库搜索结果（search_images 工具返回） */
  search_results?: SearchResultImage[];
  search_query?: string;
  search_total?: number;

  /** 模板推荐结果（search_templates 工具返回） */
  template_recommendations?: TemplateRecommendation[];
  template_query?: string;

  /** Skill runtime artifacts（前端运行态，用于展示 Apify refresh / playbook 等） */
  skill_artifacts?: ChatSkillArtifact[];

  /** Explore -> Create 阶段交接摘要（前端本地消息） */
  explore_handoff?: ExploreHandoff;
}

/** 素材库搜索结果图片 */
export interface SearchResultImage {
  id: string;
  url: string;
  thumbnail_url: string;
  description: string;
  retrieval_evidence?: {
    contract_version: 'reference-evidence-v1';
    source_kind: 'public_gallery_search';
    selection_state: 'candidate_only';
  };
}

/** 模板推荐结果（search_templates 工具返回） */
export interface TemplateRecommendation {
  id: string;
  name: string;
  name_zh: string;
  description: string;
  description_zh: string;
  cover_url: string;
  demo_images: Array<{ url: string; label?: string }>;
  category: string;
  capability: string;
  tags: string[];
  usage_count: number;
}

/** AI 提出的待确认执行计划 */
export interface PendingPlanStep {
  description: string;
  prompt?: string;
  capability: string;
  credits: number;
  reference_image_urls?: string[];
  duration?: string;
  directorBeatId?: string;
  referenceProvenance?: DirectorPlanReferenceProvenance[];
}

export interface PlanVideoGenerationRequest {
  video_capability_revision_seen: string;
  model_id: string;
  operation: 'text_to_video' | 'image_to_video' | 'video_to_video';
  reference_mode:
    | 'none'
    | 'first_frame'
    | 'last_frame'
    | 'first_last_frame'
    | 'multi_reference'
    | 'video_reference'
    | 'universal_reference';
  route_policy: string;
  duration_seconds: number;
  resolution: string;
  aspect_ratio: VideoAspectRatio | null;
  provider_prompt_enhance: VideoProviderPromptEnhance | null;
  generate_audio: boolean;
}

export interface PlanExecutionSettings {
  contract_version: 'plan-execution-settings-v1';
  reference_context?: 'current_turn' | null;
  mode?: ChatMode;
  video_duration?: VideoDuration;
  video_sub_mode?: VideoSubMode;
  video_aspect_ratio?: VideoAspectRatio | null;
  video_provider?: VideoProvider;
  kling_model?: KlingVideoModel;
  kling_generate_audio?: boolean;
  provider_prompt_enhance?: VideoProviderPromptEnhance | null;
  video_capability_revision_seen?: string;
  kling_resolution?: KlingVideoResolution;
  veo_quality?: VeoQuality;
  seedance_model?: SeedanceModel;
  seedance_resolution?: SeedanceResolution;
  image_model?: ImageModel;
  image_resolution?: ImageResolution;
  image_aspect_ratio?: ImageAspectRatio;
  midjourney_quality?: MidjourneyQuality;
  midjourney_hd?: boolean;
  midjourney_draft?: boolean;
  requested_output_count?: ImageOutputLimit;
  video_generation_request?: PlanVideoGenerationRequest;
  resolved_video_generation_contract?: Record<string, unknown>;
}

export interface PendingPlan {
  steps: PendingPlanStep[];
  total_credits: number;
  summary: string;
  consistency_note?: string;
  subject_type?: 'person' | 'outfit' | 'product' | 'scene';
  execution_settings?: PlanExecutionSettings;
  selected_step_indices?: number[];
  edited_prompts?: Record<number, string>;
  confirmed?: boolean;
  declined?: boolean;
  credits_insufficient?: boolean;
}

// ============================================
// 节点引用
// ============================================

export type ConversationCapabilitySlotKey =
  | 'product'
  | 'person'
  | 'pose'
  | 'subject'
  | 'outfit'
  | 'design'
  | 'garment'
  | 'garment1'
  | 'garment2'
  | 'garment3'
  | 'garment4'
  | 'garment5'
  | 'pattern'
  | 'sketch'
  | 'reference';

export interface AttachedNodeRef {
  nodeId: string;
  mediaType: 'video' | 'image' | 'audio' | '3d_model';
  /** Explore 能力输入槽位；用于跨入口保持“产品 / 人物 / 姿势”等语义，而不是依赖附件顺序。 */
  capabilitySlotKey?: ConversationCapabilitySlotKey;
  /** 附件来源：画布节点 or 资产/上传素材 */
  attachmentSource?: 'canvas' | 'asset';
  /** 原始素材来源标签；旧检索节点缺 provenance 时用于 fail-closed。 */
  sourceLabel?: string;
  /** 检索素材只有经过用户显式选择后才可成为生成证据。 */
  referenceEvidence?: {
    contractVersion: 'reference-evidence-v1';
    sourceKind: 'public_gallery_search' | 'public_gallery_browse';
    selectionState: 'user_confirmed';
    candidateId: string;
  };
  /** 图片/视频/模型的实际 URL */
  mediaUrl: string;
  /** 对应素材 ID，用于视频封面、流式播放等 asset 级兜底 */
  assetId?: string;
  /** 视频参考素材时长（秒）；后端计费仍以所属 asset/canvas 记录为权威。 */
  durationSeconds?: number;
  /** Generated final-image task used for an explicit frozen video continuation. */
  sourceTaskId?: string;
  generationConstraintsRef?: {
    schema_version: 'generation_constraints_v1';
    snapshot_id: string;
    snapshot_fingerprint: string;
  };
  /** 缩略图 URL（对话中预览用） */
  thumbnail: string;
  /** 用户可选别名 */
  label?: string;
  /** P0: 风格分析结果（上传后自动分析） */
  styleAnalysis?: DirectorVisualMetadata;
  /** AI 生成溯源：生成此图的 prompt */
  generationPrompt?: string;
  /** AI 生成溯源：生成此图的能力 ID */
  generationCapability?: string;
  /** 元素拆解父节点 ID（存在则为拆解子节点，不应作为 lookbook 引用） */
  decompositionParentId?: string;
}

// ============================================
// 点击标注识别
// ============================================

/** 画布标注模式：点击图片后 VLM 识别出的部件 */
export interface IdentifiedPart {
  id: string;
  /** 显示序号 ①②③... */
  markerNumber: number;
  /** 当前界面语言下的显示标签 */
  label: string;
  /** 中文标签 */
  labelZh?: string;
  /** 英文标签（给 AI 用） */
  labelEn: string;
  /** 类别 */
  category: 'clothing' | 'accessory' | 'body' | 'background' | 'object';
  /** 归一化坐标 0-1 */
  x: number;
  y: number;
  /** 所属图片 node ID */
  nodeId: string;
  /** 原图 URL（用于缩略图预览） */
  imageUrl: string;
  /** 识别状态 */
  status: 'loading' | 'done' | 'error';
  /** 是否已插入到 textarea 文本中 */
  inserted?: boolean;
  /** 局部裁剪参考图 URL（后端裁剪上传后返回） */
  cropUrl?: string;
  /** 裁剪区域归一化坐标 */
  cropBbox?: { x1: number; y1: number; x2: number; y2: number };
}

// ============================================
// 执行计划卡片
// ============================================

/**
 * 单个任务的执行计划卡片数据。
 *
 * 对应后端 backend/app/services/canvas_chat_service.py → TaskPlanData 模型。
 * 修改字段时需同步更新后端 Pydantic 定义。
 */
export interface TaskPlan {
  /** 任务类型中文标签（如"AI 生图"、"图生视频"） */
  taskType: string;
  /** 参数摘要（prompt 截断） */
  params: string;
  /** 积分消耗 */
  credits: number;
  /** 预计耗时（如"10-30 秒"） */
  estimatedTime: string;
  /** 输出类型 */
  outputType: 'image' | 'video';
  /** 时长（视频任务） */
  duration?: string;
  /** 警告信息（如低分辨率） */
  warning?: string;
  /** 关联的任务 ID（用于跟踪状态） */
  taskId?: string;
}

// ============================================
// 生成结果
// ============================================

export type GeneratedResultStatus = 'generating' | 'done' | 'failed' | 'cancelled';
export type FinalImageQualityStatus = 'pending' | 'passed' | 'failed' | 'inconclusive' | 'unknown' | 'error';

export interface GeneratedCandidateImage {
  index: number;
  url: string;
  assetId?: string;
  outputId?: string;
}

export interface GeneratedResult {
  taskId: string;
  type: 'image' | 'video';
  previewUrl: string;
  aspectRatio?: string;
  generationPrompt?: string;
  status: GeneratedResultStatus;
  /** Final server-side content QA outcome; independent of delivery status. */
  finalQualityStatus?: FinalImageQualityStatus;
  finalVerificationStatus?: string;
  selectedAttempt?: 'initial' | 'directed_repair' | string;
  qualitySelectionReason?: string;
  /** Safely persisted initial image shown while authoritative QA is still running. */
  previewReady?: boolean;
  previewRevision?: number;
  lifecycleRevision?: number;
  serverUpdatedAt?: string | number;
  phase?: import('@/lib/task-lifecycle-contract').TaskLifecyclePhase;
  eta?: import('@/lib/task-lifecycle-contract').TaskEta;
  providerTerminal?: boolean;
  providerBoundary?: import('@/lib/task-lifecycle-contract').ProviderBoundary;
  cancellationState?: import('@/lib/task-lifecycle-contract').CancellationState;
  refundStatus?: import('@/lib/task-lifecycle-contract').RefundStatus;
  /** 0-100 */
  progress?: number;
  /** 使用的 AI 能力 */
  capability: string;
  /** AI 输出资产 ID（部分任务可能先返回 asset，再由前端拼接展示 URL） */
  outputAssetId?: string;
  /** One provider task can deliver multiple durable user-visible images. */
  candidateImages?: GeneratedCandidateImage[];
  /** 输出缩略图/视频封面 */
  thumbnailUrl?: string;
  /** lookbook anchor chain 占位任务：真实 taskId 尚未创建 */
  chainPending?: boolean;
  /** 生成后在画布上的节点 ID */
  canvasNodeId?: string;
  /** 错误信息（失败时） */
  error?: string;
  /** 前端运行时：用户点击 👎 折叠此结果（不持久化） */
  dismissed?: boolean;
  /** 宫格拆分时的单元格位置 */
  gridCell?: { row: number; col: number };
  skillRunId?: string;
  skillId?: string;
  /** 视频导演 Skill 的产物阶段，用于区分分镜参考与最终成片。 */
  directorStage?: 'keyframes' | 'clips' | 'assembly' | string;
  batchId?: string;
  lane?: SkillResultLane;
  qualityScore?: number;
  artifactIds?: string[];
  sourceTrendSnapshotIds?: string[];
  optionalVideoContinuation?: boolean;
  /** Recovery-only projection for legacy product-detail retries. */
  productTarget?: import('@/lib/api/canvas-chat').ProductTargetSelection;
  requestedOutputCount?: number;
  generationIndex?: number;
  /** 品牌故事版总图任务：结果只回填故事版 slots，不创建普通图片节点。 */
  storyboardBoard?: boolean;
  /** 品牌故事版独立参考图任务：结果只回填对应锚点，不创建普通图片节点。 */
  storyboardPanel?: boolean;
  storyboardId?: string;
  storyboardAnchorId?: string;
  storyboardLayout?: { rows: number; cols: number };
  sequenceOrder?: number;
  displayDirection?: 'right_to_left' | 'left_to_right';
  /** 元素拆解：元素名称 */
  _elementName?: string;
  /** 元素拆解：元素类型 (face/garment/accessory) */
  _elementType?: string;
}

// ============================================
// 思考步骤
// ============================================

export interface ThinkingStep {
  id?: string;
  trace_id?: string;
  label: string;
  status: 'done' | 'in_progress' | 'error';
  detail?: string;
  phase?: 'intake' | 'media_analysis' | 'strategy' | 'execution' | 'workflow' | 'result' | string;
  kind?: 'summary' | 'analysis' | 'task' | 'artifact' | 'decision' | 'workflow' | string;
  started_at?: number | string;
  completed_at?: number | string;
  summary?: string;
  evidence?: string[];
  action_label?: string;
  narrative?: string;
  narrative_delta?: string;
  render_mode?: 'timeline' | 'narrative' | string;
  revision?: number;
}

// ============================================
// Token 消耗
// ============================================

export interface TokenUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

// ============================================
// SSE 流事件
// ============================================

export type ChatStreamEventType =
  | 'heartbeat'       // 保活心跳
  | 'thinking'        // 思考步骤更新
  | 'content'         // 文字内容（增量）
  | 'message_id'      // 后端预创建消息后推送真实 DB ID
  | 'suggestions'     // 跟进建议（主回复后）
  | 'plan_proposed'   // AI 提出待确认执行计划
  | 'task_preparing'  // AI 工具调用已解析，任务即将创建（前端先放占位节点）
  | 'task_created'    // AI 任务已创建，开始生成
  | 'task_progress'   // 生成进度更新
  | 'task_completed'  // 生成完成
  | 'task_failed'     // 生成失败
  | 'skill_artifact'  // Skill 运行产物（趋势刷新、playbook、prompt matrix 等）
  | 'skill_run_started'
  | 'skill_artifact_created'
  | 'skill_batch_started'
  | 'skill_batch_item_updated'
  | 'search_result'   // 素材库搜索结果
  | 'template_result' // 模板推荐结果
  | 'done'            // 响应结束
  | 'error';          // 错误

export interface ChatStreamPlanData {
  steps: PendingPlanStep[];
  total_credits: number;
  summary: string;
  consistency_note?: string;
  subject_type?: 'person' | 'outfit' | 'product' | 'scene';
  execution_settings?: PlanExecutionSettings;
  auto_execute?: boolean;
}

export interface ChatStreamSearchData {
  images: SearchResultImage[];
  total: number;
  query: string;
}

export interface ChatStreamTemplateData {
  templates: TemplateRecommendation[];
  query: string;
}

export interface ChatSkillArtifact {
  artifact_type: string;
  id?: string;
  run_id?: string;
  title?: string;
  summary?: string;
  provider?: string;
  category?: string;
  platform?: string;
  content_json?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  fresh_refresh_status?: 'completed' | 'pending' | 'failed' | 'timeout' | 'no_seeds' | string;
  snapshot_source?: string;
  snapshot_freshness_window?: string;
  raw_item_count?: number;
  candidate_count?: number;
  snapshot_count?: number;
  sample_items?: Array<{
    term?: string;
    platform?: string;
    post_count?: number;
    engagement_count?: number;
    sample_urls?: string[];
    sample_image_urls?: string[];
    sample_captions?: string[];
    sample_items?: unknown[];
    [key: string]: unknown;
  }>;
  trend_ingestion_run_ids?: string[];
  creative_transfer?: Record<string, unknown>;
  evidence_frames?: string[];
  source_video_url?: string;
  analysis_status?: string;
  [key: string]: unknown;
}

export type ChatStreamSkillArtifactData = ChatSkillArtifact;

export interface ChatSkillRunEventData {
  skillRunId?: string;
  skillId?: string;
  status?: string;
  title?: string;
  summary?: string;
  selectedSkillIds?: string[];
  artifactIds?: string[];
  batchId?: string;
  lane?: SkillResultLane;
  itemCount?: number;
  items?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface ChatStreamHeartbeatData {
  ts: number;
  phase?: 'history_summary' | 'llm' | string;
}

export interface ChatStreamEvent {
  event: ChatStreamEventType;
  data: ChatStreamThinkingData
    | ChatStreamContentData
    | ChatStreamSuggestionsData
    | ChatStreamPlanData
    | ChatStreamTaskData
    | ChatStreamSearchData
    | ChatStreamTemplateData
    | ChatStreamSkillArtifactData
    | ChatSkillRunEventData
    | ChatStreamDoneData
    | ChatStreamErrorData
    | ChatStreamHeartbeatData;
}

export interface ChatStreamThinkingData {
  step: ThinkingStep;
}

export interface ChatStreamContentData {
  delta: string;        // 增量文本
  message_id?: string;  // 最终消息 ID（done 时返回）
}

export interface ChatStreamSuggestionsData {
  suggestions: (string | ActionSuggestion)[];
}

/** 结构化创作建议（Create 入口的意图分析返回） */
export interface ActionSuggestion {
  type: 'action';
  label: string;            // "Lookbook 生成"
  description: string;      // "生成主题系列时尚大片"
  capability: string;       // "generate_lookbook"
  triggerMessage: string;   // 点击后自动发送的消息
  targetMode?: ChatMode;
  targetVideoSubMode?: VideoSubMode;
}

export interface ChatStreamTaskData {
  taskId: string;
  capability: string;
  status: GeneratedResultStatus;
  finalQualityStatus?: FinalImageQualityStatus;
  finalVerificationStatus?: string;
  selectedAttempt?: 'initial' | 'directed_repair' | string;
  qualitySelectionReason?: string;
  progress?: number;
  previewUrl?: string;
  previewReady?: boolean;
  previewRevision?: number;
  outputAssetId?: string;
  candidateImages?: GeneratedCandidateImage[];
  thumbnailUrl?: string;
  canvasNodeId?: string;
  error?: string;
  code?: string;
  maxReferenceImages?: number;
  actualReferenceImages?: number;
  imageModel?: ImageModel | string;
  preparingId?: string;
  outputType?: 'image' | 'video';
  generationIndex?: number;
  duration?: string;
  aspectRatio?: string;
  chainPending?: boolean;
  prompt?: string;
  attachedNodes?: AttachedNodeRef[];
  skillRunId?: string;
  skillId?: string;
  directorStage?: 'keyframes' | 'clips' | 'assembly' | string;
  batchId?: string;
  lane?: SkillResultLane;
  qualityScore?: number;
  artifactIds?: string[];
  sourceTrendSnapshotIds?: string[];
  storyboardBoard?: boolean;
  storyboardPanel?: boolean;
  storyboardId?: string;
  storyboardAnchorId?: string;
  storyboardLayout?: { rows: number; cols: number };
  sequenceOrder?: number;
  displayDirection?: 'right_to_left' | 'left_to_right';
  generationReadiness?: {
    schema_version: 'generation_readiness_v1';
    outcome: 'ready' | 'degraded' | 'needs_input';
    can_generate: boolean;
    requires_user_input: boolean;
    blocking_reason_codes: string[];
    advisory_reason_codes: string[];
  };
  /** needs_input is a request state, not a fabricated failed task card. */
  suppressResultCard?: boolean;
  /** The current task produces an image; video requires a later explicit send. */
  optionalVideoContinuation?: boolean;
  /** 执行计划卡片数据 */
  plan?: TaskPlan;
  /** 宫格拆分结果（grid_split 时立即返回） */
  gridResults?: Array<{ url: string; width: number; height: number; row: number; col: number }>;
}

export interface ChatStreamDoneData {
  message_id: string;
  thread_id?: string;
  token_usage?: TokenUsage;
  /** Durable request state already persisted before this terminal SSE frame. */
  request_status?: 'in_progress' | 'completed' | 'failed' | 'cancelled' | string;
  error?: string;
  code?: string;
  generationReadiness?: ChatStreamTaskData['generationReadiness'];
  clarification?: {
    required: true;
    code?: string;
    missing_fields: string[];
    policy_ids: string[];
  };
  /** Backend has chain continuation tasks being created in background */
  _chain_pending?: boolean;
}

export interface ChatStreamErrorData {
  message: string;
  code?: string;
}
